import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react-wrapper';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { UserContext } from 'contexts/UserContext';
import {
    Dialog,
    DialogDescription,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { cn } from 'shadcn/lib/utils';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import { Notifications } from 'shared/components/Notifications';

import { BulkShrinkSummary } from './BulkShrinkSummary';

import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-horizon.css';

function AddBulkShrinkModal({ adjustmentApi, getReasonsForCategory, isOpen, onOpenChange, searchItems }) {
    const { user } = useContext(UserContext);
    const [showConfirm, setShowConfirm] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [validationErrors, setValidationErrors] = useState([]);

    const hotRef = useRef(null);
    const [gridData, setGridData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [changedRows, setChangedRows] = useState(new Set());
    const [submissionResults, setSubmissionResults] = useState(null);
    const initialEmptyRows = 10;

    const reasonCodesCacheRef = useRef({});

    const getReasonCodes = useCallback(
        async (category) => {
            if (!category) {
                setNotification({ text: 'No category provided', level: 'error' });
                return [];
            }
            if (reasonCodesCacheRef.current[category]) {
                return reasonCodesCacheRef.current[category];
            }
            try {
                const reasons = await getReasonsForCategory(category);
                reasonCodesCacheRef.current[category] = reasons;
                return reasons;
            } catch (error) {
                setNotification({ text: `Error fetching reason code for category: ${category}`, level: 'error' });
                return [];
            }
        },
        [getReasonsForCategory],
    );

    // Ensure Handsontable context menu appears above the dialog and captures clicks
    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.setAttribute('data-id', 'hot-contextmenu-zfix');
        styleEl.textContent = `
            .htContextMenu, .htMenu {
                z-index: 2000 !important;
                pointer-events: auto !important;
            }
        `;
        document.head.appendChild(styleEl);
        return () => {
            if (styleEl && styleEl.parentNode) {
                styleEl.parentNode.removeChild(styleEl);
            }
        };
    }, []);

    const columns = useMemo(
        () => [
            {
                data: 'itemName',
                type: 'dropdown',
                width: 500,
                source: (query, callback) => {
                    const results = searchItems(query);
                    callback(results.map((item) => item.itemName));
                },
            },
            {
                data: 'sku',
                type: 'dropdown',
                width: 125,
                source: (query, callback) => {
                    const results = searchItems(query);
                    callback(results.map((item) => item.sku));
                },
            },
            {
                data: 'category',
                type: 'text',
                readOnly: true,
            },
            {
                data: 'reasonCode',
                type: 'dropdown',
                width: 300,
                async source(query, callback) {
                    const { row } = this;
                    const category = this.instance.getDataAtRowProp(row, 'category');
                    if (!category) {
                        callback([]);
                        return;
                    }
                    const reasons = await getReasonCodes(category);
                    callback(reasons);
                },
                strict: true,
                allowInvalid: false,
                filter: false,
            },
            { data: 'qtyUnits', type: 'numeric' },
            { data: 'notes', type: 'text' },
        ],
        [getReasonCodes, searchItems],
    );

    const colHeaders = useMemo(() => ['Item Name', 'SKU', 'Category', 'Reason Code', 'Units', 'Notes'], []);

    const generateEmptyRow = useCallback(
        () => ({
            itemName: '',
            sku: '',
            category: '',
            reasonCode: '',
            qtyUnits: null,
            notes: '',
        }),
        [],
    );

    useEffect(() => {
        setGridData(Array.from({ length: initialEmptyRows }, () => generateEmptyRow()));
    }, [initialEmptyRows, generateEmptyRow]);

    const isRowEmpty = useCallback((rowData) => {
        const result = ['itemName', 'sku', 'category', 'reasonCode', 'qtyUnits', 'notes'].every((key) => {
            const value = rowData[key];
            return key === 'qtyUnits'
                ? value === null || value === undefined
                : value === '' || value === null || value === undefined;
        });
        return result;
    }, []);

    const addRow = useCallback(
        (count = 1) => {
            setGridData((prev) => {
                const newRows = Array.from({ length: count }, () => generateEmptyRow());
                const newData = [...prev, ...newRows];
                setTimeout(() => {
                    const hot = hotRef.current?.hotInstance;
                    if (hot) {
                        // Select the first of the newly added rows
                        hot.selectCell(prev.length, 0);
                    }
                }, 50);
                return newData;
            });
        },
        [generateEmptyRow],
    );

    const nonEmptyRows = useMemo(() => gridData.filter((row) => !isRowEmpty(row)), [gridData, isRowEmpty]);

    const validateRows = useCallback(() => {
        const errors = [];
        nonEmptyRows.forEach((row, idx) => {
            const rowErrors = [];
            if (!row.itemName) rowErrors.push('Item Name');
            if (!row.sku) rowErrors.push('SKU');
            if (!row.category) rowErrors.push('Category');
            if (!row.reasonCode) rowErrors.push('Reason Code');

            const qtyUnits = parseFloat(row.qtyUnits);
            const hasValidQty = Number.isFinite(qtyUnits) && qtyUnits >= 0;

            if (!hasValidQty) rowErrors.push('Quantity Units)');

            if (rowErrors.length > 0) {
                errors.push(`Row ${idx + 1} is missing: ${rowErrors.join(', ')}`);
            }
        });
        setValidationErrors(errors);
        return errors.length === 0;
    }, [nonEmptyRows]);

    const handleModalClose = useCallback(
        (wasSubmitted = false) => {
            setValidationErrors([]);
            setChangedRows(new Set());
            setSubmissionResults(null);
            const initial = Array.from({ length: initialEmptyRows }, () => generateEmptyRow());
            setGridData(initial);
            const hot = hotRef.current?.hotInstance;
            if (hot) hot.loadData(initial);
            onOpenChange(false, wasSubmitted);
        },
        [generateEmptyRow, initialEmptyRows, onOpenChange],
    );

    const handleSubmit = useCallback(async () => {
        if (nonEmptyRows.length === 0) return;

        if (!validateRows()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const data = {
                facilityId: user.facilityID,
                rows: nonEmptyRows.map((row) => ({
                    externalSku: row.sku,
                    reason: row.reasonCode,
                    qtyUnits: row.qtyUnits,
                    notes: row.notes,
                })),
            };

            const res = await adjustmentApi.createBulkShrink(data);
            if (res.msg !== 'Success') {
                throw new Error(res.error);
            }

            setSubmissionResults(res.data);
        } catch (e) {
            setValidationErrors([e.message]);
        } finally {
            setIsSubmitting(false);
        }
    }, [nonEmptyRows, handleModalClose, user, validateRows]);

    const handleCloseClick = () => {
        if (hasPendingChanges) {
            setShowConfirm(true);
        } else {
            handleModalClose(false);
        }
    };
    const confirmClose = () => {
        setShowConfirm(false);
        handleModalClose(false);
    };
    const cancelClose = () => setShowConfirm(false);

    const afterChange = useCallback(
        function (changes, source) {
            if (!changes || source === 'loadData' || source === 'updateData' || source === 'formatDate') {
                return;
            }

            setGridData((prev) => {
                const copy = [...prev];
                changes.forEach(([rowIndex, prop, _oldValue, newValue]) => {
                    if (!copy[rowIndex]) return;
                    copy[rowIndex] = { ...copy[rowIndex], [prop]: newValue };
                });
                return copy;
            });

            const modifiedRows = [...new Set(changes.map((c) => c[0]))];
            setChangedRows((prev) => {
                const s = new Set(prev);
                modifiedRows.forEach((ri) => s.add(ri));
                return s;
            });

            if (source !== 'auto-update') {
                const hot = this;
                changes.forEach(([row, prop, _oldValue, newValue]) => {
                    if (prop === 'itemName' || prop === 'sku') {
                        const currentItemName = hot.getDataAtRowProp(row, 'itemName');
                        const currentSku = hot.getDataAtRowProp(row, 'sku');

                        if (!currentItemName && !currentSku) {
                            hot.setDataAtRowProp(row, 'category', '', 'auto-update');
                            hot.setDataAtRowProp(row, 'reasonCode', '', 'auto-update');
                            return;
                        }

                        if (newValue) {
                            const results = searchItems(newValue);
                            const exact = results.find((i) => i.itemName === newValue || i.sku === newValue);
                            if (exact) {
                                if (prop === 'itemName' && exact.itemName === newValue) {
                                    hot.setDataAtRowProp(row, 'sku', exact.sku, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                    hot.setDataAtRowProp(row, 'reasonCode', '', 'auto-update');
                                } else if (prop === 'sku' && exact.sku === newValue) {
                                    hot.setDataAtRowProp(row, 'itemName', exact.itemName, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                    hot.setDataAtRowProp(row, 'reasonCode', '', 'auto-update');
                                }
                            }
                        }
                    } else if (prop === 'category') {
                        hot.setDataAtRowProp(row, 'reasonCode', '', 'auto-update');
                    }
                });
            }
        },
        [searchItems],
    );

    const hasPendingChanges = changedRows.size > 0;
    const nonEmptyChangedRows = Array.from(changedRows).filter((ri) => gridData[ri] && !isRowEmpty(gridData[ri]));

    const hotSettings = useMemo(
        () => ({
            licenseKey: 'non-commercial-and-evaluation',
            height: 'auto',
            width: '100%',
            stretchH: 'all',
            autoColumnSize: true,
            rowHeaders: true,
            colHeaders,
            columns,
            manualRowResize: true,
            manualColumnResize: true,
            wordWrap: true,
            manualRowMove: false,
            manualColumnMove: false,
            columnSorting: false,
            dropdownMenu: false,
            filters: false,
            navigableHeaders: true,
            headerClassName: 'htLeft',
            className: 'ht-theme-horizon',
            readOnly: isSubmitting,
            minRows: initialEmptyRows,
            afterChange,
            contextMenu: ['row_above', 'row_below', 'remove_row'],
        }),
        [addRow, colHeaders, columns, gridData.length, isSubmitting, afterChange],
    );

    return (
        <>
            <Dialog open={isOpen} modal>
                <DialogPortal>
                    <DialogOverlay className="z-[1280]" />
                    <DialogPrimitive.Content
                        className={cn(
                            'fixed top-1/2 left-1/2 z-[1290] -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white shadow-lg p-0 overflow-auto',
                        )}
                        onEscapeKeyDown={(e) => e.preventDefault()}
                        // onPointerDownOutside={(e) => e.preventDefault()}
                    >
                        <div className="resize both overflow-auto min-w-[400px] min-h-[300px] w-[90vw] max-w-[1600px] h-[550px] p-6">
                            <Notifications options={notification} />
                            <DialogHeader>
                                <DialogTitle>Bulk Shrink</DialogTitle>
                                <DialogDescription>
                                    Add rows with the item, reason code, and quantity to create pending bulk shrink
                                    requests.
                                </DialogDescription>
                            </DialogHeader>

                            {validationErrors.length > 0 && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                    <div className="font-medium text-red-800 mb-1">
                                        Please fix the following errors:
                                    </div>
                                    <ul className="list-disc list-inside text-sm text-red-700">
                                        {validationErrors.map((error, idx) => (
                                            <li key={idx}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {submissionResults ? (
                                <BulkShrinkSummary results={submissionResults} onClose={() => handleModalClose(true)} />
                            ) : (
                                <>
                                    <div className="overflow-auto">
                                        <HotTable ref={hotRef} data={gridData} {...hotSettings} />
                                    </div>

                                    <div className="handsontable-actions flex justify-between items-center gap-4 pt-4">
                                        <div className="handsontable-actions-left">
                                            <button
                                                type="button"
                                                className="ht-button ht-button-add-row"
                                                onClick={() => addRow(10)}
                                                disabled={isSubmitting}
                                                title="Add 10 new rows"
                                            >
                                                + Add 10 Rows
                                            </button>
                                        </div>

                                        <div className="handsontable-actions-right flex items-center gap-4">
                                            {hasPendingChanges && nonEmptyChangedRows.length > 0 && (
                                                <span className="ht-pending-changes">
                                                    {nonEmptyChangedRows.length} row
                                                    {nonEmptyChangedRows.length !== 1 ? 's' : ''} modified
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="ht-button ht-button-submit-all"
                                                onClick={handleSubmit}
                                                disabled={
                                                    !hasPendingChanges ||
                                                    nonEmptyChangedRows.length === 0 ||
                                                    isSubmitting
                                                }
                                                title={
                                                    nonEmptyChangedRows.length === 0
                                                        ? 'No changes to submit'
                                                        : 'Submit all changes'
                                                }
                                            >
                                                {isSubmitting ? 'Submitting...' : 'Submit All'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            <button
                                type="button"
                                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground cursor-pointer"
                                onClick={handleCloseClick}
                                aria-label="Close modal"
                            >
                                <XIcon className="h-6 w-6" strokeWidth={2} />
                            </button>
                        </div>
                    </DialogPrimitive.Content>
                </DialogPortal>
            </Dialog>

            <ConfirmDialog open={showConfirm} onConfirm={confirmClose} onCancel={cancelClose} />
        </>
    );
}

export { AddBulkShrinkModal };
